// Import model
import { ContactTask } from '../models/ContactTask';

// Import base class serializer
import { ObjectTaskSerializer } from './ObjectTaskSerializer';

// Import dependency types (models and enums used in properties)
import { Contact } from '../models/Contact';
import { ReferencedObject } from '../models/ReferencedObject';

// Import dependency serializers (for complex types)
import { ContactSerializer } from './ContactSerializer';
import { ReferencedObjectSerializer } from './ReferencedObjectSerializer';

// Import derived type serializers (for polymorphic deserialization)

/**
 * Serializer for ContactTask - handles JSON serialization and deserialization with type validation
 */
export class ContactTaskSerializer {
  /**
   * Deserializes a JSON string into a ContactTask instance
   */
  static deserialize(json: string): ContactTask {
    const obj = JSON.parse(json);
    return this.fromObject(obj);
  }

  /**
   * Deserializes a plain object into a ContactTask instance
   */
  static fromObject(obj: any): ContactTask {
    if (!obj) {
      throw new Error('Cannot deserialize null or undefined to ContactTask');
    }


    const instance: ContactTask = {
      assignedUserId: obj['assignedUserId'] as string | undefined,
      assignedUserIdName: obj['assignedUserId|Name'] as string | undefined,
      dueDate: obj['dueDate'] as string | undefined,
      priority: obj['priority'] as string | undefined,
      name: obj['name'] as string | undefined,
      description: obj['description'] as string | undefined,
      flowId: obj['flowId'] as string | undefined,
      flowIdName: obj['flowId|Name'] as string | undefined,
      objectStatusId: obj['objectStatusId'] as string | undefined,
      objectStatusIdName: obj['objectStatusId|Name'] as string | undefined,
      id: obj['_id'] as string | undefined,
      createdDate: obj['createdDate'] as string | undefined,
      lastModifiedDate: obj['lastModifiedDate'] as string | undefined,
      content: obj['content'] as string | undefined,
      contentType: obj['contentType'] as string | undefined,
      creatorId: obj['creatorId'] as string | undefined,
      creatorIdName: obj['creatorId|Name'] as string | undefined,
      entityId: obj['entityId'] as string | undefined,
      entityIdName: obj['entityId|Name'] as string | undefined,
      isActive: obj['isActive'] as boolean | undefined,
      relatedObjects: obj['relatedObjects'] as string | undefined,
      parent: obj['parent'] ? ReferencedObjectSerializer.fromObject(obj['parent']) : undefined as any,
      parentObjectType: obj['parentObjectType'] as string,
      parentObjectTypeName: obj['parentObjectType|Name'] as string | undefined,
      parentObjectId: obj['parentObjectId'] as string | undefined,
      d328A451: obj['_d328a451'] as any | undefined,
      contact: obj['contact'] ? ContactSerializer.fromObject(obj['contact']) : undefined as any,
    };

    return instance;
  }

  /**
   * Deserializes a JSON string into an array of ContactTask instances
   */
  static deserializeArray(json: string): ContactTask[] {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) {
      throw new Error('Expected JSON array');
    }
    return arr.map(obj => this.fromObject(obj));
  }

  /**
   * Serializes a ContactTask instance to a JSON string
   */
  static serialize(instance: ContactTask): string {
    return JSON.stringify(this.toObject(instance));
  }

  /**
   * Converts a ContactTask instance to a plain object
   */
  static toObject(instance: ContactTask): any {
    return {
      'assignedUserId': instance.assignedUserId,
      'assignedUserId|Name': instance.assignedUserIdName,
      'dueDate': instance.dueDate,
      'priority': instance.priority,
      'name': instance.name,
      'description': instance.description,
      'flowId': instance.flowId,
      'flowId|Name': instance.flowIdName,
      'objectStatusId': instance.objectStatusId,
      'objectStatusId|Name': instance.objectStatusIdName,
      '_id': instance.id,
      'createdDate': instance.createdDate,
      'lastModifiedDate': instance.lastModifiedDate,
      'content': instance.content,
      'contentType': instance.contentType,
      'creatorId': instance.creatorId,
      'creatorId|Name': instance.creatorIdName,
      'entityId': instance.entityId,
      'entityId|Name': instance.entityIdName,
      'isActive': instance.isActive,
      'relatedObjects': instance.relatedObjects,
      'parent': instance.parent ? ReferencedObjectSerializer.toObject(instance.parent) : undefined,
      'parentObjectType': instance.parentObjectType,
      'parentObjectType|Name': instance.parentObjectTypeName,
      'parentObjectId': instance.parentObjectId,
      '_d328a451': instance.d328A451,
      'contact': instance.contact ? ContactSerializer.toObject(instance.contact) : undefined,
    };
  }

  /**
   * Serializes an array of ContactTask instances to a JSON string
   */
  static serializeArray(instances: ContactTask[]): string {
    return JSON.stringify(instances.map(inst => this.toObject(inst)));
  }
}
